import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useExpediente } from "@/context/ExpedienteContext";
import { Spinner, AutosaveNote } from "@/components/common";
import { LockBanner } from "@/components/portalCommon";
import { lbl, SECTION_LABELS } from "@/constants/labels";
import { Save, Plus, Trash2 } from "lucide-react";

const SIMPLE = ["datos_personales", "salud", "perfil"];
const LIST = ["familia", "contactos_emergencia", "referencias_laborales", "referencias_familiares", "referencias_no_familiares", "academica"];
const TEXTAREAS = ["perfil_ocupacional", "habilidades_persona", "accidente_detalle", "antecedente_detalle", "funciones"];
const DATES = ["fecha_diligenciamiento", "fecha_nacimiento", "fecha_ingreso", "fecha_expedicion"];

// Definido FUERA del componente: si se define dentro, cada render crea un tipo nuevo
// y React desmonta el input en cada tecla (la letra desaparece y se pierde el foco).
function Field({ campo, value, onChange, meta, locked }) {
  const common = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-vino-500 focus:ring-2 focus:ring-vino-500/20 disabled:bg-slate-50";
  if (campo === "tipo_documento")
    return <select disabled={locked} value={value || ""} onChange={(e) => onChange(e.target.value)} className={common}><option value="">Selecciona</option>{meta.tipos_documento.map((t) => <option key={t}>{t}</option>)}</select>;
  if (campo === "estado_civil")
    return <select disabled={locked} value={value || ""} onChange={(e) => onChange(e.target.value)} className={common}><option value="">Selecciona</option>{meta.estados_civiles.map((t) => <option key={t}>{t}</option>)}</select>;
  if (campo === "grupo_sanguineo")
    return <select disabled={locked} value={value || ""} onChange={(e) => onChange(e.target.value)} className={common} data-testid="hv-grupo-sanguineo"><option value="">Selecciona</option>{["O+", "O-", "A+", "A-", "B+", "B-", "AB+", "AB-"].map((t) => <option key={t}>{t}</option>)}</select>;
  if (campo === "ultimo_nivel_estudios")
    return <select disabled={locked} value={value || ""} onChange={(e) => onChange(e.target.value)} className={common}><option value="">Selecciona</option>{meta.niveles_estudio.map((t) => <option key={t}>{t}</option>)}</select>;
  if (TEXTAREAS.includes(campo))
    return <textarea disabled={locked} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value.toUpperCase())} className={common} />;
  const mayus = !DATES.includes(campo) && !campo.includes("correo");
  return <input disabled={locked} type={DATES.includes(campo) ? "date" : "text"} value={value || ""} onChange={(e) => onChange(mayus ? e.target.value.toUpperCase() : e.target.value)} className={common} />;
}

export default function HojaVida() {
  const { exp, meta, reload } = useExpediente();
  const [hv, setHv] = useState(null);
  const [tab, setTab] = useState("datos_personales");
  const [saving, setSaving] = useState(false);
  const [auto, setAuto] = useState(null); // null | "guardando" | "error" | hora "HH:MM"
  const [solicitando, setSolicitando] = useState(false);
  const savedRef = useRef("");
  const hvRef = useRef(null);
  hvRef.current = hv;

  useEffect(() => {
    if (exp) {
      const copia = JSON.parse(JSON.stringify(exp.hoja_vida || {}));
      savedRef.current = JSON.stringify(copia);
      setHv(copia);
    }
  }, [exp]);

  // Autoguardado cada 30 s cuando hay cambios; no interrumpe la digitación ni recarga la página.
  useEffect(() => {
    if (exp?.bloqueado) return undefined;
    const t = setInterval(async () => {
      const actual = hvRef.current;
      if (!actual) return;
      const snap = JSON.stringify(actual);
      if (snap === savedRef.current) return;
      setAuto("guardando");
      try {
        await api.put("/portal/hoja-vida", { hoja_vida: actual });
        savedRef.current = snap;
        setAuto(new Date().toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" }));
      } catch {
        setAuto("error");
      }
    }, 30000);
    return () => clearInterval(t);
  }, [exp?.bloqueado]);

  if (!exp || !meta || !hv) return <div className="flex h-64 items-center justify-center text-vino-700"><Spinner className="h-6 w-6" /></div>;

  const locked = exp.bloqueado && !solicitando;
  const pendiente = exp.solicitud_pendiente;
  const tabs = [...SIMPLE, ...LIST].filter((s) => meta.secciones[s] || meta.listas[s]);

  const setSimple = (sec, campo, val) => setHv((p) => ({ ...p, [sec]: { ...(p[sec] || {}), [campo]: val } }));
  const setList = (sec, idx, campo, val) => setHv((p) => { const arr = [...(p[sec] || [])]; arr[idx] = { ...arr[idx], [campo]: val }; return { ...p, [sec]: arr }; });
  const addRow = (sec) => setHv((p) => ({ ...p, [sec]: [...(p[sec] || []), {}] }));
  const delRow = (sec, idx) => setHv((p) => ({ ...p, [sec]: (p[sec] || []).filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    try {
      if (exp.bloqueado) {
        await api.post("/portal/hoja-vida/solicitud", { hoja_vida: hv });
        toast.success("Solicitud enviada. HUMAN revisará y aprobará los cambios.");
        setSolicitando(false);
      } else {
        await api.put("/portal/hoja-vida", { hoja_vida: hv });
        savedRef.current = JSON.stringify(hv);
        setAuto(null);
        toast.success("Avance guardado");
      }
      reload();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Mi hoja de vida</h1>
        <p className="mt-1 text-sm text-slate-500">Puedes guardar y continuar después. Sin pruebas psicotécnicas en esta etapa.</p>
      </div>
      {exp.bloqueado && !solicitando && (
        <div className="space-y-3">
          <LockBanner />
          {pendiente
            ? <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800" data-testid="hv-solicitud-pendiente">Tienes una solicitud de actualización ({pendiente.cambios?.length || 0} cambio(s)) pendiente de aprobación por HUMAN.</p>
            : null}
          <button onClick={() => setSolicitando(true)} data-testid="hv-solicitar-cambio" className="rounded-lg border border-vino-200 bg-white px-4 py-2 text-sm font-semibold text-vino-700 hover:bg-vino-50">{pendiente ? "Modificar mi solicitud (reemplaza la pendiente)" : "Solicitar actualización de datos"}</button>
        </div>
      )}
      {solicitando && <p className="rounded-lg border border-vino-100 bg-vino-50/60 px-4 py-2.5 text-sm text-vino-800" data-testid="hv-modo-solicitud">Modo solicitud: corrige los datos y envíalos. Los cambios se aplicarán cuando HUMAN los apruebe.</p>}

      <div className="flex gap-1 overflow-x-auto border-b border-slate-200 pb-px">
        {tabs.map((s) => (
          <button key={s} onClick={() => setTab(s)} data-testid={`hv-tab-${s}`}
            className={`whitespace-nowrap border-b-2 px-3.5 py-2.5 text-sm font-medium ${tab === s ? "border-vino-700 text-vino-800" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {SECTION_LABELS[s] || s}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        {SIMPLE.includes(tab) && meta.secciones[tab] && (
          <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
            {meta.secciones[tab].map((campo) => (
              <div key={campo} className={TEXTAREAS.includes(campo) ? "sm:col-span-2 lg:col-span-3" : ""}>
                <label className="mb-1 block text-xs font-medium text-slate-500">{lbl(campo)}</label>
                <Field campo={campo} value={hv[tab]?.[campo]} onChange={(v) => setSimple(tab, campo, v)} meta={meta} locked={locked} />
              </div>
            ))}
          </div>
        )}

        {LIST.includes(tab) && meta.listas[tab] && (
          <div className="space-y-4">
            {(hv[tab] || []).length === 0 && <p className="text-sm text-slate-400">Aún no has agregado registros.</p>}
            {(hv[tab] || []).map((row, idx) => (
              <div key={idx} className="rounded-xl border border-slate-200 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{SECTION_LABELS[tab]} {idx + 1}</span>
                  {!locked && <button onClick={() => delRow(tab, idx)} className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
                </div>
                <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
                  {meta.listas[tab].map((campo) => (
                    <div key={campo} className={campo === "funciones" ? "sm:col-span-2 lg:col-span-3" : ""}>
                      <label className="mb-1 block text-xs font-medium text-slate-500">{lbl(campo)}</label>
                      <Field campo={campo} value={row[campo]} onChange={(v) => setList(tab, idx, campo, v)} meta={meta} locked={locked} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {!locked && <button onClick={() => addRow(tab)} data-testid={`hv-add-${tab}`} className="flex items-center gap-2 rounded-lg border border-dashed border-slate-300 px-4 py-2 text-sm font-medium text-vino-700 hover:bg-vino-50"><Plus className="h-4 w-4" /> Agregar</button>}
          </div>
        )}
      </div>

      {!locked && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          {!solicitando && <AutosaveNote auto={auto} testid="hv-autosave-status" />}
          {solicitando && <button onClick={() => { setSolicitando(false); setHv(JSON.parse(JSON.stringify(exp.hoja_vida || {}))); }} data-testid="hv-cancelar-solicitud" className="rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancelar</button>}
          <button onClick={save} disabled={saving} data-testid="hv-guardar" className="flex items-center gap-2 rounded-lg bg-vino-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-vino-800 disabled:opacity-60">
            {saving ? <Spinner /> : <Save className="h-4 w-4" />} {solicitando ? "Enviar solicitud a HUMAN" : "Guardar avance"}
          </button>
        </div>
      )}
    </div>
  );
}
